import type { ModuleAccessLevel } from '../config/moduleAccessPolicy';
import { moduleAccessLevelShortLabel } from '../config/moduleAccessPolicy';
import type { RoleModuleAccessSummary, RoleSubmoduleAccessSummary } from '../services/api';

export function submoduleKey(moduleCode: string, submoduleId: string): string {
  return `${moduleCode}::${submoduleId}`;
}

export function parseSubmoduleKey(key: string): { moduleCode: string; submoduleId: string } | null {
  const [moduleCode, submoduleId] = key.split('::');
  if (!moduleCode || !submoduleId) return null;
  return { moduleCode, submoduleId };
}

export function aggregateModuleLevel(
  submodules: RoleSubmoduleAccessSummary[]
): ModuleAccessLevel | 'custom' {
  if (!submodules.length) return 'none';
  const levels = submodules.map((sub) => sub.access_level as ModuleAccessLevel);
  const unique = Array.from(new Set(levels.filter((level) => level !== 'none')));
  if (!unique.length) return 'none';
  if (unique.length === 1) return unique[0];
  return 'custom';
}

export function flattenSubmoduleAccess(
  modules: RoleModuleAccessSummary[]
): Record<string, ModuleAccessLevel> {
  const next: Record<string, ModuleAccessLevel> = {};
  for (const module of modules) {
    for (const sub of module.submodules ?? []) {
      next[submoduleKey(module.module_code, sub.submodule_id)] =
        (sub.access_level as ModuleAccessLevel) || 'none';
    }
  }
  return next;
}

export function buildSubmoduleSavePayload(
  modules: RoleModuleAccessSummary[],
  levels: Record<string, ModuleAccessLevel>
) {
  const submodules = modules
    .filter((module) => module.enabled)
    .reduce<Array<{ module_code: string; submodule_id: string; access_level: ModuleAccessLevel }>>(
      (acc, module) => {
        for (const sub of module.submodules ?? []) {
          acc.push({
            module_code: module.module_code,
            submodule_id: sub.submodule_id,
            access_level: levels[submoduleKey(module.module_code, sub.submodule_id)] ?? 'none',
          });
        }
        return acc;
      },
      []
    );
  return { submodules };
}

export function applyModuleLevelToSubmodules(
  module: RoleModuleAccessSummary,
  level: ModuleAccessLevel,
  levels: Record<string, ModuleAccessLevel>
): Record<string, ModuleAccessLevel> {
  const next = { ...levels };
  for (const sub of module.submodules ?? []) {
    if (level === 'none') {
      next[submoduleKey(module.module_code, sub.submodule_id)] = 'none';
      continue;
    }
    if (level === 'read' && !sub.supports_read) {
      next[submoduleKey(module.module_code, sub.submodule_id)] = 'none';
      continue;
    }
    if (level === 'write' && !sub.supports_write) {
      next[submoduleKey(module.module_code, sub.submodule_id)] = sub.supports_read ? 'read' : 'none';
      continue;
    }
    next[submoduleKey(module.module_code, sub.submodule_id)] = level;
  }
  return next;
}

export function moduleLevelLabel(level: ModuleAccessLevel | 'custom'): string {
  if (level === 'custom') return 'Custom';
  return moduleAccessLevelShortLabel(level);
}
